import { useCallback, useContext, useRef, useState } from 'react';
import { Button } from '@fluentui/react-components';

import { ToastContext } from '@shared/contexts';
import { TOptionsDialogState } from '@shared/types';
import { USER_GROUPS, SignInContext } from '@modules/users';

import { useUpload } from '../api/items.api';
import { IFolder } from '../types/folders.types';
import { AccessType } from '@modules/access/types';
import {
  validateUploadPermissions,
  validateFilenames,
  validateImportRestriction,
  validateFileSizes,
  validateFileCount,
  validateTotalSize,
  filterIgnoredFiles,
  isDocumentImportFile,
  getUploadRelativePath,
  TValidationError,
} from '../helpers/uploadValidation.helper';

type TUseFileUploadParams = {
  folder: IFolder;
  isBlockFolderType: boolean;
  countriesFolderType: boolean;
  snapshotsFolderType: boolean;
};

const useFileUpload = ({
  folder,
  isBlockFolderType,
  countriesFolderType,
  snapshotsFolderType,
}: TUseFileUploadParams) => {
  const { user, organization } = useContext(SignInContext);
  const { show } = useContext(ToastContext);
  const upload = useUpload();
  // Shared guard for all upload triggers; a state flag would miss clicks that land before React re-renders.
  const uploadInProgressRef = useRef(false);

  const [optionsDialogState, setOptionsDialogState] = useState<TOptionsDialogState | undefined>(undefined);

  const handleFilesUpload = useCallback(
    async (files: File[]) => {
      // Helper to show validation errors and return early
      const showValidationError = (error: TValidationError): boolean => {
        if (error) {
          show(error.type, error.message);
          return true;
        }
        return false;
      };

      // Permission checks
      const permissionError = validateUploadPermissions(
        isBlockFolderType,
        countriesFolderType,
        snapshotsFolderType,
        folder.accessRight,
        user.groups,
        AccessType.EDIT,
        USER_GROUPS.AUTHOR
      );
      if (showValidationError(permissionError)) return;

      if (files.length === 0) {
        show('error', 'Empty folder(s) cannot be uploaded.');
        return;
      }

      // Get relative paths
      const relPaths = files.map(getUploadRelativePath);

      // Filter system files first (before validations so they don't count against limits)
      const { importableFiles, importableRelPaths, ignoredCount } = filterIgnoredFiles(files, relPaths);

      if (ignoredCount > 0) {
        if (importableFiles.length === 0) {
          show('error', 'Only system files selected (e.g., ~$...). Nothing to import.');
          return;
        }
        show('info', `${ignoredCount} system file${ignoredCount === 1 ? ' was' : 's were'} skipped (e.g., ~$...).`);
      }

      // Validate filenames (on importable files only)
      if (showValidationError(validateFilenames(importableRelPaths))) return;

      // Organization import restrictions
      const { fileNameRegex, whitelistedMails, message } = organization.mock?.importRestriction ?? {};
      if (
        showValidationError(
          validateImportRestriction(importableFiles, fileNameRegex, whitelistedMails, user.mail, message)
        )
      )
        return;

      if (showValidationError(validateFileCount(importableFiles.length))) return;
      if (showValidationError(validateTotalSize(importableFiles))) return;

      // Build FormData
      const formData = new FormData();
      importableFiles.forEach((file, i) => {
        formData.append('files', file, encodeURIComponent(importableRelPaths[i]));
      });

      const closeDialog = () => {
        setOptionsDialogState((current) => (current ? { ...current, isOpen: false } : current));
      };

      // Upload function
      const uploadFn = async ({
        documentImportConfigId,
        isStaticFiles,
      }: {
        documentImportConfigId?: string;
        isStaticFiles?: boolean;
      } = {}): Promise<void> => {
        if (showValidationError(validateFileSizes(importableFiles, importableRelPaths, Boolean(isStaticFiles)))) {
          return;
        }
        if (uploadInProgressRef.current) return;

        uploadInProgressRef.current = true;
        closeDialog();

        try {
          await upload.mutateAsync({ folder, formData, documentImportConfigId, isStaticFiles });
        } finally {
          uploadInProgressRef.current = false;
        }
      };

      const hasDocxImports = importableFiles.some((file, i) => isDocumentImportFile(file, importableRelPaths[i]));

      const shouldShowDialog =
        organization.documentImportConfigs.length > 1 ||
        (organization.documentImportConfigs.length <= 1 && hasDocxImports);

      if (!shouldShowDialog) {
        await uploadFn();
        return;
      }

      const options =
        organization.documentImportConfigs.length > 0
          ? organization.documentImportConfigs.map((config) => ({
              title: config.label,
              value: config._id,
              onClick: () => {
                void uploadFn({ documentImportConfigId: config._id });
              },
            }))
          : [
              {
                title: 'Default import',
                value: 'default-document-import',
                onClick: () => {
                  void uploadFn();
                },
              },
            ];

      const dialogState: TOptionsDialogState = {
        isOpen: true,
        title:
          organization.documentImportConfigs.length > 0
            ? 'Select an import configuration.'
            : 'Choose how to import Word files.',
        options,
        footer: hasDocxImports ? (
          <Button
            appearance="subtle"
            size="small"
            onClick={() => {
              void uploadFn({ isStaticFiles: true });
            }}
          >
            Import as static files
          </Button>
        ) : undefined,
      };

      setOptionsDialogState(dialogState);
    },
    [folder, isBlockFolderType, countriesFolderType, snapshotsFolderType, user, organization, show, upload]
  );

  return { handleFilesUpload, optionsDialogState, setOptionsDialogState };
};

export default useFileUpload;
